import { useState, useEffect, useMemo } from 'react';
import { useOrganizations, useDeleteOrganization } from '@/hooks';
import { useScopeTypes } from '@/hooks/useScopeTypes';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { Button } from '@/components/ui/button';
import { FilterBar } from '@/components/ui/filter-bar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronUp, ChevronDown, ChevronsUpDown, Building2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Organization } from '@/lib/schemas';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useReferrerPage } from '@/hooks/useReferrerPage'; // 9.15c: Navigation/pagination fix
import { PaginationInfo } from '@/components/PaginationInfo';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { calculatePaginationValues } from '@/lib/pagination';

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization | null>(null);

  // Read filters from URL params
  const search = searchParams.get('search') || '';
  const roleFilter = searchParams.get('role') || '';
  const categoryFilter = searchParams.get('category') || ''; // Phase 9.13a: category_id or 'uncategorized'
  const sortBy = searchParams.get('sort') || 'name';
  const sortOrder = searchParams.get('order') || 'asc';

  // Phase 6: Pagination state management in URL
  const { page, limit, offset, setPage } = usePaginationState(50);

  // 9.15c: Store page before navigating to detail page
  const { storeCurrentPage } = useReferrerPage('organizations');

  // Phase 9.13a: Fetch categories (scope types) for the filter dropdown
  const { data: scopeTypes } = useScopeTypes();

  // Fetch organizations with backend filtering and pagination (Phase 6)
  // Phase 9.13a: Category filter - can be a specific category_id or 'uncategorized'
  // Phase 9.13a: Role filter - can be a specific role or 'notype' for no role assigned
  const { data, isLoading, error } = useOrganizations({
    search: search || undefined,
    role: roleFilter && roleFilter !== 'notype' ? roleFilter : undefined,
    has_role: roleFilter === 'notype' ? false : undefined,
    category_id: categoryFilter && categoryFilter !== 'uncategorized' ? categoryFilter : undefined,
    has_category: categoryFilter === 'uncategorized' ? false : undefined,
    limit,
    offset,
  }, {
    keepPreviousData: true, // Smooth page transitions
  });

  const organizationsRaw = data?.organizations || [];
  const total = data?.total || 0;

  // MED-09: Client-side sorting (backend doesn't support sorting for organizations yet)
  const organizations = useMemo(() => {
    const sorted = [...organizationsRaw];
    sorted.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortBy === 'created_at') {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [organizationsRaw, sortBy, sortOrder]);

  // Calculate pagination values
  const paginationValues = calculatePaginationValues(total, offset, limit);

  // Handle search change
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    // Phase 6: Reset to page 1 when search changes (combined with search update to avoid race condition)
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // MED-09: Handle role filter change
  const handleRoleFilterChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('role', value);
    } else {
      newParams.delete('role');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Phase 9.13a: Handle category filter change
  const handleCategoryFilterChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('category', value); // Either a category_id or 'uncategorized'
    } else {
      newParams.delete('category');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // MED-09: Handle sort change (clicking column headers)
  const handleSort = (column: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (sortBy === column) {
      // Toggle order if same column
      newParams.set('order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to asc for name
      newParams.set('sort', column);
      newParams.set('order', column === 'name' ? 'asc' : 'desc');
    }
    setSearchParams(newParams);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    // Clear all params (search, page, role, sort, etc.)
    setSearchParams(new URLSearchParams());
  };

  // MED-09: Count active filters for badge
  // Phase 9.13a: Include category filter in count
  const activeFilterCount = (search ? 1 : 0) + (roleFilter ? 1 : 0) + (categoryFilter ? 1 : 0);

  // Phase 6: Handle page overflow - redirect to last valid page if current page exceeds total
  useEffect(() => {
    if (!isLoading && data && paginationValues.totalPages > 0) {
      if (page > paginationValues.totalPages) {
        setPage(paginationValues.totalPages);
      }
    }
  }, [page, paginationValues.totalPages, isLoading, data, setPage]);

  // Phase 6: Use delete hook with optimistic updates and total count handling
  const deleteMutation = useDeleteOrganization();

  const handleDeleteClick = (organization: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    setOrganizationToDelete(organization);
  };

  const confirmDelete = () => {
    if (organizationToDelete) {
      deleteMutation.mutate(organizationToDelete.id);
      setOrganizationToDelete(null); // Clear dialog state
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Contacts">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout
        title="Contacts"
        actions={
          <Button onClick={() => navigate('/organizations/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Client
          </Button>
        }
      >
        <div className="mb-4">
          <p className="text-muted-foreground">
            Manage your contacts and client information
          </p>
        </div>

        {/* MED-09: Filter Bar with Role Filter */}
        {/* Phase 9.13a: Added Category Filter */}
        <div className="flex items-center gap-4 mb-4">
          <FilterBar
            search={search}
            onSearchChange={handleSearchChange}
            placeholder="Search clients..."
            onClearAll={handleClearAll}
            activeFilterCount={activeFilterCount}
          />
          {/* Role Filter Dropdown */}
          <Select value={roleFilter || 'all'} onValueChange={handleRoleFilterChange}>
            <SelectTrigger className="w-[160px]" data-testid="role-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="consultant">Consultants</SelectItem>
              <SelectItem value="government">Government</SelectItem>
              <SelectItem value="self">Self (Internal)</SelectItem>
              <SelectItem value="notype">No type</SelectItem>
            </SelectContent>
          </Select>
          {/* Phase 9.13a: Category Filter Dropdown */}
          <Select
            value={categoryFilter || 'all'}
            onValueChange={handleCategoryFilterChange}
          >
            <SelectTrigger className="w-[180px]" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              {scopeTypes?.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Error State */}
        {error && (
          <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Failed to load clients: {(error as Error).message}
            </p>
          </div>
        )}

        {/* Table */}
        {!error && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* MED-09: Sortable Name column */}
                  <TableHead
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortBy === 'name' ? (
                        sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />
                      )}
                    </div>
                  </TableHead>
                  {/* Phase 9.22c: Legal Entity column */}
                  <TableHead>Legal Entity</TableHead>
                  {/* Phase 9.22c: DBA/Establishments column */}
                  <TableHead>DBAs</TableHead>
                  {/* Phase 9.14: Email/Phone hidden - default to primary contact */}
                  <TableHead>Payment Terms</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      {search ? (
                        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                          <p>No clients found</p>
                          <p className="text-sm mt-1">Try adjusting your search</p>
                        </div>
                      ) : (
                        <EmptyState
                          icon={Building2}
                          title="No contacts yet"
                          description="Add your clients, consultants, and contractors"
                          actionLabel="Add Client"
                          actionUrl="/organizations/new"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  organizations.map((organization: Organization) => (
                    <TableRow
                      key={organization.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        storeCurrentPage(page); // 9.15c: Store page for back navigation
                        navigate(`/organizations/${organization.id}`);
                      }}
                    >
                      <TableCell className="font-medium">{organization.name}</TableCell>
                      {/* Phase 9.22c: Legal Entity column */}
                      <TableCell className="text-muted-foreground">
                        {organization.legal_name || '-'}
                      </TableCell>
                      {/* Phase 9.22c: DBAs/Establishments count */}
                      <TableCell className="text-muted-foreground">
                        {organization.establishments_count ? organization.establishments_count : '-'}
                      </TableCell>
                      {/* Phase 9.14: Email/Phone hidden - default to primary contact */}
                      <TableCell className="text-muted-foreground">
                        {organization.payment_terms ? `Net ${organization.payment_terms}` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/organizations/${organization.id}/edit`);
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteClick(organization, e)}
                            disabled={deleteMutation.isPending}
                            className="text-destructive hover:text-destructive h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Phase 6: Pagination Footer */}
        {!error && total > 0 && (
          <div className="flex items-center justify-between">
            <PaginationInfo
              total={total}
              start={paginationValues.start}
              end={paginationValues.end}
              itemName="clients"
            />

            {paginationValues.totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage(page - 1)}
                      className={!paginationValues.hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>

                  {/* First page */}
                  {paginationValues.currentPage > 2 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(1)} className="cursor-pointer">
                        1
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {/* Left ellipsis */}
                  {paginationValues.currentPage > 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* Previous page */}
                  {paginationValues.hasPreviousPage && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(page - 1)} className="cursor-pointer">
                        {page - 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {/* Current page */}
                  <PaginationItem>
                    <PaginationLink isActive className="cursor-default">
                      {page}
                    </PaginationLink>
                  </PaginationItem>

                  {/* Next page */}
                  {paginationValues.hasNextPage && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setPage(page + 1)} className="cursor-pointer">
                        {page + 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {/* Right ellipsis */}
                  {paginationValues.currentPage < paginationValues.totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* Last page */}
                  {paginationValues.currentPage < paginationValues.totalPages - 1 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setPage(paginationValues.totalPages)}
                        className="cursor-pointer"
                      >
                        {paginationValues.totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setPage(page + 1)}
                      className={!paginationValues.hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        )}
      </DashboardLayout>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!organizationToDelete} onOpenChange={() => setOrganizationToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{organizationToDelete?.name}"? This action cannot be undone.
              {organizationToDelete && (
                <span className="block mt-2 text-orange-600">
                  Note: This client cannot be deleted if they have associated projects.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
